import { TierListTemplate } from "@/types/tier-list";

// Current template version
export const CURRENT_TEMPLATE_VERSION = 1;

/**
 * Upgrade a template from v1 to v2
 * (Placeholder for future use when v2 is defined)
 */
function upgradeV1ToV2(template: TierListTemplate): TierListTemplate {
  // When v2 is defined, implement the upgrade logic here
  // For now, just return the template as-is with updated version
  return {
    ...template,
    version: 2,
  };
}

/**
 * Upgrade a template from v2 to v3
 * (Placeholder for future use when v3 is defined)
 */
function upgradeV2ToV3(template: TierListTemplate): TierListTemplate {
  // When v3 is defined, implement the upgrade logic here
  return {
    ...template,
    version: 3,
  };
}

/**
 * Map of upgrade functions
 * Key is the source version, value is the function to upgrade to the next version
 */
const upgradeFunctions: Record<
  number,
  (template: TierListTemplate) => TierListTemplate
> = {
  1: upgradeV1ToV2,
  2: upgradeV2ToV3,
  // Add more upgrade functions here as versions increase
};

/**
 * Automatically upgrade a template to the current version
 * Runs all necessary upgrade scripts sequentially (v1 -> v2 -> v3, etc.)
 */
export function upgradeTemplate(
  template: TierListTemplate
): TierListTemplate {
  let upgradedTemplate = { ...template };
  let currentVersion = template.version;

  // If no version specified, assume v1 for backward compatibility
  if (currentVersion === undefined) {
    currentVersion = 1;
    upgradedTemplate.version = 1;
  }

  // Apply upgrades sequentially until we reach the current version
  while (currentVersion < CURRENT_TEMPLATE_VERSION) {
    const upgradeFunction = upgradeFunctions[currentVersion];

    if (!upgradeFunction) {
      console.warn(
        `No upgrade function found for version ${currentVersion}. Stopping upgrade process.`
      );
      break;
    }

    upgradedTemplate = upgradeFunction(upgradedTemplate);
    currentVersion = upgradedTemplate.version;
  }

  return upgradedTemplate;
}

/**
 * Validate that a template has the required fields for its version
 */
export function validateTemplate(template: TierListTemplate): boolean {
  // Check required fields for v1
  if (!template.name || !template.tiers || !Array.isArray(template.items)) {
    return false;
  }

  // Validate tiers
  for (const tier of template.tiers) {
    if (
      !tier.id ||
      !tier.label ||
      tier.priority === undefined ||
      !tier.color ||
      !tier.color.label ||
      !tier.color.background
    ) {
      return false;
    }
  }

  // Validate items
  for (const item of template.items) {
    if (!item.id || !item.name || !item.image) {
      return false;
    }
  }

  return true;
}
